import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Download, Trash2, Shield, FileText, Clock, AlertTriangle } from 'lucide-react';
import { useOptimizedAuth } from '@/hooks/useOptimizedAuth';
import { useSimpleProfile } from '@/hooks/useSimpleProfile';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const DataManagement = () => {
  const { user } = useOptimizedAuth();
  const { profile } = useSimpleProfile();
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDataExport = async () => {
    setIsExporting(true);
    try {
      // Gather user data from various sources
      const userData = {
        profile: profile,
        account: {
          email: user?.email,
          created_at: user?.created_at,
          last_sign_in: user?.last_sign_in_at,
        },
        export_date: new Date().toISOString(),
        export_type: 'complete_user_data'
      };

      // Create and download JSON file
      const dataStr = JSON.stringify(userData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `user_data_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "Data Exported",
        description: "Your data has been exported and downloaded.",
      });
    } catch (error) {
      toast({
        title: "Export Failed",
        description: "Failed to export your data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleAccountDeletion = async () => {
    setIsDeleting(true);
    try {
      // First update profile to mark for deletion
      if (profile?.id) {
        const { error: profileError } = await supabase
          .from('profiles')
          .update({ 
            account_status: 'pending_deletion',
            updated_at: new Date().toISOString()
          })
          .eq('id', profile.id);

        if (profileError) {
          throw profileError;
        }
      }

      toast({
        title: "Account Deletion Requested",
        description: "Your account has been marked for deletion. Our team will process this request within 7 days. You can cancel this request by contacting support.",
      });
    } catch (error) {
      toast({
        title: "Deletion Failed",
        description: "Failed to process deletion request. Please contact support.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const dataCategories = [
    {
      title: "Profile Information",
      description: "Personal details, preferences, and account settings",
      icon: FileText,
      size: "~2KB"
    },
    {
      title: "Order History",
      description: "Purchase history, shipping information, and order details",
      icon: Clock,
      size: "~15KB"
    },
    {
      title: "Activity Logs",
      description: "Login history, security events, and account activity",
      icon: Shield,
      size: "~5KB"
    }
  ];

  return (
    <div className="space-y-6">
      {/* Data Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Your Data Overview
          </CardTitle>
          <CardDescription>
            See what data we store and manage your privacy preferences
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {dataCategories.map((category, index) => (
            <div key={category.title}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <category.icon className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{category.title}</p>
                    <p className="text-xs text-muted-foreground">{category.description}</p>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground">{category.size}</span>
              </div>
              {index < dataCategories.length - 1 && <Separator className="mt-4" />}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Data Export */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Export Your Data
          </CardTitle>
          <CardDescription>
            Download a copy of all your data in JSON format
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium mb-2">What's included in your export:</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Profile information and preferences</li>
              <li>• Order history and shipping addresses</li>
              <li>• Account settings and privacy preferences</li>
              <li>• Login history and security events</li>
            </ul>
          </div>
          
          <Button
            onClick={handleDataExport}
            disabled={isExporting}
            className="w-full"
          >
            <Download className="h-4 w-4 mr-2" />
            {isExporting ? "Preparing Export..." : "Download My Data"}
          </Button>
        </CardContent>
      </Card>

      {/* Account Deletion */}
      <Card className="border-destructive/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Delete Account
          </CardTitle>
          <CardDescription>
            Permanently delete your account and all associated data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-lg">
            <h4 className="font-medium text-destructive mb-2">⚠️ This action cannot be undone</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Your profile and personal information will be deleted</li>
              <li>• Order history will be anonymized but retained for legal purposes</li>
              <li>• You will lose access to your account immediately</li>
              <li>• This process cannot be reversed</li>
            </ul>
          </div>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="w-full">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete My Account
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete your account and remove all your data from our servers. 
                  This action cannot be undone. Your order history will be anonymized but retained for legal compliance.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleAccountDeletion}
                  disabled={isDeleting}
                  className="bg-destructive hover:bg-destructive/90"
                >
                  {isDeleting ? "Processing..." : "Yes, Delete Account"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  );
};

export default DataManagement;